import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
);

const Subsection = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    {children}
  </div>
);

const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-3">{children}</p>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-6 text-gray-700 space-y-1.5 mb-3">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">FieldHouse Terms of Use</h1>
          <p className="text-sm text-gray-500 mb-10">Last Updated: July 3, 2026</p>

          <P>
            {'These Terms of Use ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and FieldHouse Solutions, LLC ("Company," "we," "us," or "our") governing your access to and use of the Company\'s websites, applications, software, and related services (collectively, the "Services").'}
          </P>
          <P>
            <strong>
              {'BY ACCESSING OR USING THE SERVICES, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE, YOU MAY NOT ACCESS OR USE THE SERVICES.'}
            </strong>
          </P>

          <Section title="1. The Services">
            <P>
              {'The Services provide a digital platform for youth and amateur sports organizations, teams, coaches, parents, and administrators to manage sports participation and performance, including:'}
            </P>
            <Ul items={[
              'Team, league, and organization administration',
              'Scheduling, rosters, drafts, and player evaluations',
              'Communication and coordination between users',
              'Storage and sharing of video, images, and performance data',
              'Artificial intelligence-based analysis of athletic performance, including sports mechanics and movement analysis',
            ]} />
            <P>
              {'The Services are intended solely for organized youth and amateur sports use and are not intended as medical, therapeutic, or professional athletic certification tools.'}
            </P>
          </Section>

          <Section title="2. Eligibility and Account Registration">
            <Subsection title="2.1 Eligibility">
              <P>
                {'You must be at least 18 years of age, or the age of majority in your jurisdiction, to create an account and use the Services.'}
              </P>
              <P>
                {'The Services may be used in connection with minors only by a parent, legal guardian, or authorized organizational representative.'}
              </P>
            </Subsection>
            <Subsection title="2.2 Account Responsibility">
              <P>{'You are responsible for:'}</P>
              <Ul items={[
                'Maintaining the confidentiality of your login credentials',
                'All activities conducted under your account',
                'Ensuring all information provided is accurate and current',
                'Ensuring you have authority to act on behalf of any organization or minor associated with your account',
              ]} />
              <P>{'We may suspend or terminate accounts for any violation of these Terms.'}</P>
            </Subsection>
          </Section>

          <Section title="3. User Roles and Authority">
            <P>{'Users may operate in one or more roles, including:'}</P>
            <Ul items={[
              'Parents / Guardians',
              'Coaches / Team Staff',
              'Organization Administrators (Leagues, Clubs, Associations)',
              'Evaluators or Authorized Personnel',
            ]} />
            <P>{'Each role represents and warrants that they have all required authority, consents, and legal rights to:'}</P>
            <Ul items={[
              'Submit or manage data relating to minors',
              'Bind an organization to applicable terms',
              'Upload or distribute content through the Services',
            ]} />
          </Section>

          <Section title="4. Minors and Parental Consent">
            <Subsection title="4.1 Parental Authorization">
              <P>
                {'If you create, manage, or sponsor a minor\'s participation in the Services, you represent and warrant that:'}
              </P>
              <Ul items={[
                'You are the minor\'s parent or legal guardian, or have lawful authorization from such parent or guardian',
                'You consent to the collection, use, and processing of the minor\'s data as described in these Terms and the Privacy Policy',
                'You are fully responsible for the minor\'s use of the Services',
              ]} />
            </Subsection>
            <Subsection title="4.2 Responsibility for Minors">
              <P>{'Parents and guardians assume full responsibility for:'}</P>
              <Ul items={[
                'Participation decisions',
                'Uploaded content and communications',
                'Any evaluations, feedback, or AI-generated insights associated with the minor',
              ]} />
            </Subsection>
          </Section>

          <Section title="5. User Content and Data Rights">
            <Subsection title="5.1 User Content">
              <P>
                {'"User Content" includes any content submitted to the Services, including but not limited to:'}
              </P>
              <Ul items={[
                'Video, images, and recordings',
                'Performance metrics and evaluations',
                'Scouting reports and coaching notes',
                'Messages, communications, and team data',
              ]} />
              <P>{'You retain ownership of your User Content.'}</P>
            </Subsection>
            <Subsection title="5.2 License to Company">
              <P>
                {'By submitting User Content, you grant the Company a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to host, store, reproduce, modify, adapt, analyze, display, and otherwise use User Content solely for the purpose of operating, improving, and providing the Services.'}
              </P>
              <P>{'This includes:'}</P>
              <Ul items={[
                'Generating analytics and performance insights',
                'Training and improving AI/ML models used in the Services, unless you opt out where required by law',
                'Displaying content to authorized users within the same team, league, or organization',
              ]} />
            </Subsection>
            <Subsection title="5.3 Organization Data Rights and Responsibilities">
              <P>{'Where User Content is submitted under an Organization account:'}</P>
              <Ul items={[
                'The Organization may control access and visibility',
                'The Organization may determine visibility of evaluations and content',
                'The Organization may remove or modify organization-generated data',
                'The Organization is responsible for ensuring lawful collection and use of all associated data',
                'The Company is not responsible for disputes between users and organizations',
              ]} />
              <P>{'Youth sports organizations using the Services represent and warrant that:'}</P>
              <Ul items={[
                'They have obtained all necessary parental consents for minors participating in their programs',
                'They are responsible for compliance with all applicable child privacy laws',
                'They have authority to upload, manage, and process minor-related data',
              ]} />
            </Subsection>
            <Subsection title="5.4 No Direct Accounts for Children Under 13">
              <P>
                {'The Services are not directed to children under the age of 13, and we do not knowingly allow children under 13 to create accounts.'}
              </P>
              <P>
                {'Accounts must be created and managed by a parent, legal guardian, or authorized organization representative.'}
              </P>
              <P>{'We do not knowingly use children\'s personal information for:'}</P>
              <Ul items={[
                'Behavioral advertising',
                'Sale of personal data',
                'Off-platform profiling unrelated to sports performance analytics',
              ]} />
            </Subsection>
            <Subsection title="5.5 Parental Consent Requirement">
              <P>{'Where personal information of a child under 13 is collected, it is collected:'}</P>
              <Ul items={[
                'Only through a parent, guardian, or authorized organization',
                'With verifiable parental consent where required by law, including the Children\'s Online Privacy Protection Act (COPPA) in the United States',
              ]} />
              <P>{'By allowing a minor to participate in the Services, you expressly consent to:'}</P>
              <Ul items={[
                'Collection of performance and sports participation data',
                'Video and image uploads of athletic activity',
                'Communication and evaluation data related to team participation',
              ]} />
            </Subsection>
            <Subsection title="5.6 Platform Rights">
              <P>{'The Company retains rights to:'}</P>
              <Ul items={[
                'Operate and improve the Services',
                'Generate aggregated analytics',
                'Use anonymized data for performance and product improvements',
                'Enforce safety, moderation, and compliance policies',
              ]} />
            </Subsection>
            <Subsection title="5.7 Data Retention">
              <P>{'We retain data:'}</P>
              <Ul items={[
                'For as long as necessary to provide the Services',
                'For legal, operational, or compliance purposes',
                'To maintain organizational continuity, such as seasons, leagues, and historical statistics',
              ]} />
            </Subsection>
            <Subsection title="5.8 Data Deletion Rights">
              <P>{'Users and organizations may request deletion of personal data subject to:'}</P>
              <Ul items={[
                'Legal retention obligations',
                'Organizational account structure',
                'Safety and fraud prevention requirements',
              ]} />
            </Subsection>
          </Section>

          <Section title="6. Artificial Intelligence Features">
            <Subsection title="6.1 Features and Functionality">
              <P>
                {'The Services may include AI-generated analysis, recommendations, scoring, or insights based on User Content.'}
              </P>
              <P>{'You acknowledge and agree that:'}</P>
              <Ul items={[
                'AI outputs are generated automatically and may be inaccurate, incomplete, or misleading',
                'AI outputs do not constitute professional coaching, medical, or performance certification advice',
                'You are solely responsible for how you interpret and use AI-generated insights',
                'The Company disclaims all liability for reliance on AI outputs',
              ]} />
            </Subsection>
            <Subsection title="6.2 No Professional Advice">
              <P>{'AI-generated outputs are:'}</P>
              <Ul items={[
                'Informational only',
                'Not medical, diagnostic, coaching certification, or safety guidance',
                'Not guaranteed to be accurate, complete, or applicable to any individual athlete',
              ]} />
              <P>
                {'You assume full responsibility for how AI outputs are interpreted and applied. To the maximum extent permitted by law, the Company disclaims all liability arising from:'}
              </P>
              <Ul items={[
                'Reliance on AI outputs',
                'Incorrect evaluations or recommendations',
                'Decisions made based on AI-assisted analysis',
              ]} />
            </Subsection>
            <Subsection title="6.3 Training and Improvement of Models">
              <P>
                {'To the extent permitted by law and subject to our Privacy Policy, we may use anonymized or aggregated User Content to:'}
              </P>
              <Ul items={[
                'Improve AI model accuracy and performance',
                'Enhance feature functionality',
                'Develop new analytics and evaluation tools',
              ]} />
              <P>{'Where required by law, users may be provided opt-out mechanisms for AI training use.'}</P>
            </Subsection>
          </Section>

          <Section title="7. Acceptable Use and Restrictions">
            <P>{'You agree not to:'}</P>
            <Ul items={[
              'Upload unlawful, defamatory, abusive, or infringing content',
              'Harass, threaten, or exploit minors',
              'Misrepresent identity or affiliation',
              'Scrape, reverse engineer, or attempt to extract source code or data',
              'Use the Services for unauthorized commercial resale or redistribution',
              'Upload content without proper rights or required consents',
              'Interfere with system integrity, security, or performance',
            ]} />
            <P>{'We may investigate and remove content or suspend access at our sole discretion.'}</P>
          </Section>

          <Section title="8. Privacy and Data Handling">
            <P>
              {'Your use of the Services is governed by our '}
              <a href="/privacy-policy" className="text-blue-600 underline">Privacy Policy</a>
              {'.'}
            </P>
            <P>{'You acknowledge that the Services may process:'}</P>
            <Ul items={[
              'Personal information',
              'Minor-related sports performance data',
              'Video and audio recordings',
              'Communications and organizational data',
            ]} />
            <P>
              <strong>{'Mobile phone numbers and SMS opt-in consent are never sold or shared with third parties for marketing purposes.'}</strong>
              {' They are used only to deliver the messages you have opted in to receive and are shared only with our SMS delivery provider as necessary to send those messages. See our '}
              <a href="/sms-compliance" className="text-blue-600 underline">SMS Compliance</a>
              {' page for details.'}
            </P>
            <P>{'We implement commercially reasonable safeguards but do not guarantee absolute security.'}</P>
          </Section>

          <Section title="9. Subscriptions and Payment">
            <Subsection title="9.1 Fees">
              <P>{'Certain features require a paid subscription. Fees will be disclosed at the time of purchase.'}</P>
            </Subsection>
            <Subsection title="9.2 Auto-Renewal">
              <P>{'Subscriptions automatically renew unless canceled prior to the renewal date.'}</P>
            </Subsection>
            <Subsection title="9.3 Cancellation">
              <P>
                {'You may cancel at any time, and cancellation will take effect at the end of the current billing period.'}
              </P>
            </Subsection>
            <Subsection title="9.4 Refunds">
              <P>
                {'Payments are non-refundable except where required by law or applicable platform, such as Apple App Store / Google Play, policies.'}
              </P>
              <P>{'We reserve the right to change pricing with notice effective for future billing cycles.'}</P>
            </Subsection>
          </Section>

          <Section title="10. Intellectual Property">
            <P>
              {'All rights, title, and interest in the Services, excluding User Content, are owned by the Company or its licensors.'}
            </P>
            <P>
              {'You are granted a limited, non-exclusive, non-transferable license to use the Services solely for their intended purpose.'}
            </P>
          </Section>

          <Section title="11. Termination">
            <P>{'We may suspend or terminate access to the Services at any time:'}</P>
            <Ul items={[
              'For violation of these Terms',
              'To comply with legal obligations',
              'To protect the integrity or security of the Services',
            ]} />
            <P>{'Upon termination, your license to use the Services will immediately cease.'}</P>
          </Section>

          <Section title="12. Disclaimers">
            <P><strong>{'THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE."'}</strong></P>
            <P>
              <strong>{'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:'}</strong>
            </P>
            <Ul items={[
              'Accuracy of analytics or AI outputs',
              'Fitness for a particular purpose',
              'Merchantability',
              'Non-infringement',
              'Uninterrupted or error-free operation',
              'Results from use of the Services',
            ]} />
          </Section>

          <Section title="13. Limitation of Liability">
            <P><strong>{'TO THE MAXIMUM EXTENT PERMITTED BY LAW:'}</strong></P>
            <Ul items={[
              'Under no circumstance shall the Company be liable to you or any third party for indirect, incidental, special, consequential, or punitive damages',
              'Under no circumstance shall the Company\'s total liability exceed the greater of $100 or the amount actually paid by you in the preceding 12 months',
            ]} />
          </Section>

          <Section title="14. Indemnification and Waiver">
            <P>
              {'You agree to defend, indemnify, and hold harmless the Company, its affiliates, licensors, service providers, officers, directors, employees, contractors, agents, successors, and assigns from any and all claims, damages, liabilities, costs, or expenses arising from:'}
            </P>
            <Ul items={[
              'Your use of the Services',
              'Your User Content',
              'Your violation of these Terms',
              'Your violation of any rights of a third party',
              'Your misuse of data relating to minors or organizations',
              'Bodily injury, including death, or property damage arising from use of the Services',
            ]} />
            <P>
              {'To the fullest extent permitted by applicable law, you acknowledge that use of the Services and participation in youth sports, athletic training, evaluations, practices, games, tournaments, and related activities involves inherent risks of injury, illness, property damage, and other losses that cannot be completely eliminated.'}
            </P>
            <P>
              {'You understand and agree that the Services include a technology platform that facilitates communications, scheduling, player evaluations, analytics, team management, and other administrative functions. The Company, and the Services it provides, do not supervise athletic activities, provide medical advice, guarantee player safety, or control the conduct of leagues, organizations, coaches, volunteers, officials, parents, players, facilities, or other third parties.'}
            </P>
            <P>
              {'Accordingly, to the fullest extent permitted by law, you voluntarily assume all risks associated with participation in youth sports and the use of the Services, and release, waive, and discharge the Company, its affiliates, licensors, service providers, officers, directors, employees, contractors, agents, successors, and assigns from any and all claims, demands, causes of action, damages, losses, liabilities, costs, and expenses arising out of or relating to:'}
            </P>
            <Ul items={[
              'Participation in any athletic activity, practice, game, tournament, camp, clinic, or event',
              'The acts or omissions of leagues, organizations, coaches, volunteers, parents, players, officials, facility operators, or any other third party',
              'Injuries, illnesses, accidents, or property damage occurring during or in connection with youth sports activities or Company\'s Services',
              'Your use of or reliance upon the Services, including information, evaluations, statistics, analytics, communications, schedules, or other content made available by the Company',
            ]} />
            <P>
              {'Nothing in this Agreement releases, limits, or excludes liability to the extent such liability cannot be waived or limited under applicable law, including liability arising from the Company\'s gross negligence, willful misconduct, fraud, or any other liability that may not legally be disclaimed.'}
            </P>
            <P>
              {'You further agree that any supervision of minors, transportation to or from athletic events, medical care, emergency response, equipment safety, and compliance with league or facility rules remain solely the responsibility of the applicable parent, legal guardian, coach, league, organization, or other responsible adult, and not the Company.'}
            </P>
          </Section>

          <Section title="15. Dispute Resolution">
            <Subsection title="15.1 Attempted Resolution and Mediation">
              <P>
                {'If any dispute arises out of or relates to these Terms or the Services, the parties shall attempt to resolve it via direct negotiation for 60 days. Any unresolved disputes must then be submitted to mediation under JAMS Rules. In the event mediation is unsuccessful, each party irrevocably submits to the exclusive jurisdiction and venue of the state and federal courts located in Fulton County, Georgia.'}
              </P>
            </Subsection>
            <Subsection title="15.2 Class Action Waiver">
              <P>
                {'You agree that disputes will be brought only in an individual capacity and not as a plaintiff or class member in any class or representative proceeding.'}
              </P>
            </Subsection>
            <Subsection title="15.3 Governing Law">
              <P>
                {'These Terms shall be governed by the laws of the State of Georgia, without regard to conflict of law principles.'}
              </P>
            </Subsection>
          </Section>

          <Section title="16. Modifications">
            <P>
              {'We may modify these Terms at any time. Continued use of the Services after changes become effective constitutes acceptance of the revised Terms.'}
            </P>
          </Section>

          <Section title="17. Privacy Policy Alignment">
            <P>
              {'Your use of the Services is also governed by our '}
              <a href="/privacy-policy" className="text-blue-600 underline">Privacy Policy</a>
              {', which describes:'}
            </P>
            <Ul items={[
              'What data we collect',
              'How we use it',
              'Who we share it with',
              'Your rights regarding your data',
            ]} />
            <P>{'In the event of conflict, the Privacy Policy governs personal data handling.'}</P>
          </Section>

          <Section title="18. Security Disclaimer">
            <P>
              {'We implement commercially reasonable safeguards to protect data, including encryption, access controls, and secure storage practices.'}
            </P>
            <P>{'However:'}</P>
            <Ul items={[
              'No system is 100% secure',
              'We cannot guarantee prevention of unauthorized access, breaches, or data loss',
              'Users are responsible for maintaining account security',
            ]} />
          </Section>

          <Section title="19. Export, Sanctions and Legal Compliance">
            <P>{'You may not use the Services if you are:'}</P>
            <Ul items={[
              'Located in a sanctioned jurisdiction',
              'Listed on any applicable government restricted party list',
              'Using the Services in violation of export control laws',
            ]} />
          </Section>

          <Section title="20. Contact">
            <P>{'For questions regarding these Terms, contact:'}</P>
            <div className="bg-gray-50 rounded-lg p-5 text-gray-700 text-sm space-y-1">
              <p><strong>FieldHouse Solutions, LLC</strong></p>
              <p>
                Email:{' '}
                <a href="mailto:support@fieldhouse.club" className="text-blue-600 underline">
                  support@fieldhouse.club
                </a>
              </p>
              <p>
                Website:{' '}
                <a href="https://www.fieldhouse.club" className="text-blue-600 underline">
                  www.fieldhouse.club
                </a>
              </p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
